import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import CachedImage from "../shared/CachedImage";
import { LabbaikItem } from "../../hooks/useLabbaikItems";
import { readableColour } from "../../utils/colourContrast";
import "./LabbaikSlide.css";

interface LabbaikColours {
  widgetBackgroundColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

interface LabbaikSlideProps {
  item: LabbaikItem;
  colours: LabbaikColours;
}

// Same colours as the salah changes and events panels. Masjids pick Secondary freely
// (some pick black on a dark widget background), so fall back until the line is readable.
function panelColours({ widgetBackgroundColor, primaryColor, secondaryColor }: LabbaikColours) {
  const background = widgetBackgroundColor || "#0F172A";
  const strip = primaryColor || "#FFC107";
  const accent = readableColour([secondaryColor, primaryColor], background);
  return { background, strip, accent };
}

function titleSize(title: string) {
  if (title.length <= 40) return 7;
  if (title.length <= 70) return 5.5;
  return 4.5;
}

function textSize(text: string) {
  if (text.length <= 120) return 4.2;
  if (text.length <= 300) return 3.4;
  return 2.6;
}

// Sized from its own box rather than vw, so it fits a full-screen slideshow and a
// small layout region alike. Mosques type the text themselves: plain text only.
export default function LabbaikSlide({ item, colours }: LabbaikSlideProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [item.imageUrl]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) =>
      setBox({ width: entry.contentRect.width, height: entry.contentRect.height }),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Area-based so wide boxes get readable type, capped so very wide or tall boxes still fit.
  const unit =
    Math.min(Math.sqrt(box.width * box.height), 1.6 * Math.min(box.width, box.height)) / 100;
  const portrait = box.height > box.width;
  const image = item.imageUrl && !imageFailed ? item.imageUrl : null;
  const poster = image && item.kind !== "appeal";
  const title = item.title || "";
  // A poster carries its own wording, so its text would only repeat it.
  const text = poster ? "" : item.text || "";

  const { background, strip, accent } = panelColours(colours);
  const style = {
    "--lb-u": `${unit}px`,
    "--lb-bg": background,
    "--lb-strip": strip,
    "--lb-accent": accent,
    "--lb-title": titleSize(title),
    "--lb-text": textSize(text),
  } as React.CSSProperties;

  return (
    <div
      ref={ref}
      className={`labbaik-slide${portrait ? " labbaik-slide--portrait" : ""}`}
      style={style}
    >
      {unit > 0 && (
        <>
          {poster && (
            <div className="labbaik-slide__poster">
              <CachedImage
                src={image}
                alt={title}
                className="labbaik-slide__poster-image"
                onError={() => setImageFailed(true)}
              />
            </div>
          )}

          <div className="labbaik-slide__body">
            {image && !poster && (
              <CachedImage
                src={image}
                alt={title}
                className="labbaik-slide__cover"
                onError={() => setImageFailed(true)}
              />
            )}
            {title && <h2 className="labbaik-slide__title">{title}</h2>}
            {item.detail && <p className="labbaik-slide__detail">{item.detail}</p>}
            {item.progressPercent != null && (
              <div className="labbaik-slide__progress">
                <div
                  className="labbaik-slide__progress-fill"
                  style={{ width: `${item.progressPercent}%` }}
                />
              </div>
            )}
            {text && <p className="labbaik-slide__text">{text}</p>}
          </div>

          {item.linkUrl && (
            <div className="labbaik-slide__qr">
              <div className="labbaik-slide__qr-code">
                <QRCodeSVG
                  value={item.linkUrl}
                  size={Math.round(unit * (portrait ? 34 : 30))}
                  level="M"
                  marginSize={2}
                />
              </div>
              {item.linkLabel && (
                <div className="labbaik-slide__qr-label">{item.linkLabel}</div>
              )}
              {item.note && <div className="labbaik-slide__note">{item.note}</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
